// Aggregator → Inbound triage (Spec §8.6 / §10.7 / Fig59). A scored,
// blind-matched inbound TABLE built from REAL data only: the portfolio
// payload's review_status==='pending' projects, ranked by their real
// top_match_score (the cross-pollination blind-overlap score written to the
// project record). P7: it NEVER fabricates a score, a row, a status, or a
// success — an unscored row shows an honest em dash / "Unscored" pill, and
// when there is nothing inbound the honest EmptyState is the deliverable.
//
// Columns: Applicant / Matched venture / Sector / Match score / Received /
// Status. Filter chips (Sector / Min score / Status) narrow what's VISIBLE
// only — the selection set is never pruned by a filter change, so bulk
// actions (Invite / Export / Tag / Clear) always act on everything the user
// actually checked, even rows a filter is currently hiding.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Inbox, Zap } from 'lucide-react'
import PageHeader from '../../components/ui/PageHeader'
import NextStepBanner from '../../components/ui/NextStepBanner'
import ContextualTip from '../../components/ui/ContextualTip'
import HelpPopover from '../../components/help/HelpPopover'
import EmptyState from '../../components/shared/EmptyState'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import InboundTriageTable from '../../components/aggregator/InboundTriageTable'
import InboundFilterChips, {
  applyInboundFilters,
  DEFAULT_INBOUND_FILTERS,
  type InboundFilterState,
} from '../../components/aggregator/InboundFilterChips'
import InboundBulkActionBar from '../../components/aggregator/InboundBulkActionBar'
import BulkInviteDialog from '../../components/aggregator/BulkInviteDialog'
import { useAggregatorAccessGate } from '../../hooks/useAggregatorAccessGate'
import { useAggregator } from '../../contexts/AggregatorContext'
import { getAggregatorPortfolioProjects, getPortfolioOverlap } from '../../services/api'
import type { AggregatorProject } from '../../types'

// Inbound = disclosures awaiting first review, ranked by their real blind-match
// score. Scored rows first (highest first); unscored (null) rows sink to the
// bottom — we never invent a score to rank them. Exported for a pure,
// provider-free unit test.
export function rankInbound(projects: AggregatorProject[]): AggregatorProject[] {
  return projects
    .filter((p) => p.review_status === 'pending')
    .slice()
    .sort((a, b) => (b.top_match_score ?? -1) - (a.top_match_score ?? -1))
}

const INBOUND_HELP = {
  title: 'Inbound triage',
  body:
    'Disclosures newly shared with your aggregator, ranked by their blind-match ' +
    'score against the rest of your book. A higher score means stronger overlap — ' +
    'start at the top. Scores are computed on privacy-preserving fingerprints, not ' +
    'the underlying documents, so you can prioritise before opening anything. Your ' +
    'selection follows you across the Sector / Min score / Status filters below — ' +
    'filtering only changes what is visible, never what is selected.',
}

export default function AggregatorInbound() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasAggregatorAccess, activeAggregatorId } = useAggregator()
  const accessGate = useAggregatorAccessGate('inbound disclosures')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState<InboundFilterState>(DEFAULT_INBOUND_FILTERS)
  // Row-level quick invite (single applicant) — reuses the same dialog/API as
  // the bulk toolbar's Invite button, just seeded with one project id.
  const [inviteProjectIds, setInviteProjectIds] = useState<string[] | null>(null)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
    enabled: hasAggregatorAccess,
  })

  const inbound = useMemo(() => rankInbound(data?.projects ?? []), [data])
  const filteredInbound = useMemo(() => applyInboundFilters(inbound, filters), [inbound, filters])

  // Shares AggregatorPortfolio's/OverlapView's exact query key — the
  // inbound-triage cross-link (design §6.3: "inbound triage cross-links
  // when a newly arrived project joins an existing group") costs no extra
  // fetch once any Overlap surface has been visited.
  const { data: overlapData } = useQuery({
    queryKey: ['portfolio-overlap', activeAggregatorId],
    queryFn: () => getPortfolioOverlap(activeAggregatorId),
    enabled: hasAggregatorAccess,
  })
  const overlappingInboundCount = useMemo(() => {
    if (!overlapData) return 0
    const inGroup = new Set<string>()
    for (const g of overlapData.groups ?? []) {
      if (g.worth_a_look) continue
      for (const id of g.member_project_ids) inGroup.add(id)
    }
    return inbound.filter((p) => inGroup.has(p.project_id)).length
  }, [overlapData, inbound])

  // Selection persists across filter changes BY DESIGN (spec: "Selection
  // persists across filters"). It is only ever pruned when the underlying
  // data itself changes — e.g. a project is no longer pending anywhere (it
  // was approved, invited, or removed) — so a stale id never lingers forever,
  // but a filter toggle alone never drops a single checked row.
  const allIdsSignature = inbound.map((p) => p.project_id).join(',')
  useEffect(() => {
    setSelectedIds((prev) => {
      const allIds = new Set(inbound.map((p) => p.project_id))
      const next = new Set([...prev].filter((id) => allIds.has(id)))
      return next.size === prev.size ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIdsSignature])

  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const visibleIds = useMemo(() => filteredInbound.map((p) => p.project_id), [filteredInbound])
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id))
  const toggleAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id))
      else visibleIds.forEach((id) => next.add(id))
      return next
    })

  const selectedProjects = useMemo(() => inbound.filter((p) => selectedIds.has(p.project_id)), [inbound, selectedIds])
  const visibleIdSet = useMemo(() => new Set(visibleIds), [visibleIds])
  const hiddenSelectedProjects = useMemo(
    () => selectedProjects.filter((p) => !visibleIdSet.has(p.project_id)),
    [selectedProjects, visibleIdSet],
  )

  const openInviteForRow = (project: AggregatorProject) => setInviteProjectIds([project.project_id])

  const invalidatePortfolio = () =>
    queryClient.invalidateQueries({ queryKey: ['aggregator-portfolio', activeAggregatorId] })

  const help = (
    <HelpPopover topic="agg-inbound" title={INBOUND_HELP.title} body={INBOUND_HELP.body} size={18} />
  )

  const showTable = hasAggregatorAccess && !isLoading && !isError && inbound.length > 0

  let content: ReactNode
  if (accessGate) {
    content = accessGate
  } else if (isLoading) {
    content = <LoadingSkeleton rows={5} type="table" />
  } else if (isError) {
    content = (
      <div className="text-center py-16" role="alert">
        <AlertCircle size={48} className="mx-auto mb-3 text-red-400" />
        <p className="text-lg font-medium text-gray-900">Couldn't load inbound disclosures</p>
        <p className="text-sm text-gray-600 mt-1">{(error as Error)?.message}</p>
        <button
          onClick={() => refetch()}
          data-testid="inbound-retry"
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
        >
          Retry
        </button>
      </div>
    )
  } else if (inbound.length === 0) {
    // Honest, P7-compliant empty state — no invented rows or scores.
    content = (
      <EmptyState
        icon={<Inbox size={28} />}
        title="No inbound disclosures yet"
        description="When organisations share new disclosures with your aggregator, they appear here — automatically ranked by their blind-match score so you can triage the strongest overlaps first."
      />
    )
  } else {
    content = (
      <>
        <div className="mb-3">
          <ContextualTip id="agg-inbound-selection-persists">
            Selecting rows here works across filters — narrowing by Sector, Min score, or Status only changes
            what's visible, never what's selected. Your count stays honest even when a filter hides some of your
            picks.
          </ContextualTip>
        </div>

        <InboundFilterChips
          rows={inbound}
          filters={filters}
          onChange={setFilters}
          visibleCount={filteredInbound.length}
          totalCount={inbound.length}
        />

        {selectedIds.size > 0 && (
          <InboundBulkActionBar
            selectedProjectIds={[...selectedIds]}
            selectedProjects={selectedProjects}
            hiddenProjects={hiddenSelectedProjects}
            activeAggregatorId={activeAggregatorId}
            onClear={() => setSelectedIds(new Set())}
            onComplete={() => {
              setSelectedIds(new Set())
              invalidatePortfolio()
            }}
          />
        )}

        {filteredInbound.length === 0 ? (
          <div
            className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500"
            data-testid="inbound-filter-empty"
          >
            No inbound disclosures match the current filters.
          </div>
        ) : (
          <InboundTriageTable
            rows={filteredInbound}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            allSelected={allVisibleSelected}
            someSelected={someVisibleSelected}
            onToggleAll={toggleAllVisible}
            onOpenRow={(id) => navigate(`/project/${id}`)}
            onInviteRow={openInviteForRow}
            canInvite={!!activeAggregatorId}
          />
        )}
      </>
    )
  }

  return (
    <div className="pb-24">
      <PageHeader
        title="Inbound triage"
        description="Scored, blind-matched disclosures newly shared with your aggregator."
        status={showTable ? { label: `${inbound.length} to triage`, tone: 'info' } : undefined}
        secondaryActions={
          <span className="inline-flex items-center gap-2">
            <span className="hidden items-center gap-1 text-sm text-slate-600 sm:inline-flex">
              <Zap size={14} className="text-iflow-500" aria-hidden /> Blind matching &amp; scoring
            </span>
            {help}
          </span>
        }
        banner={
          showTable ? (
            <NextStepBanner
              stage="Triage"
              message={
                selectedIds.size > 0
                  ? `${selectedIds.size} selected. Invite the organisations to disclose, export your shortlist, or tag them for later.`
                  : 'Select the highest-scoring disclosures, then invite the organisations to disclose or export your shortlist.'
              }
            />
          ) : undefined
        }
      />

      {overlappingInboundCount > 0 && (
        <div
          className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
          data-testid="inbound-overlap-crosslink"
        >
          <AlertCircle size={15} className="flex-shrink-0" aria-hidden />
          <span>
            {overlappingInboundCount} pending disclosure{overlappingInboundCount === 1 ? '' : 's'} already{' '}
            {overlappingInboundCount === 1 ? 'scores' : 'score'} as possible duplicated effort with an existing project.
          </span>
          <button
            onClick={() => navigate('/agg?view=overlap')}
            className="ml-auto font-semibold hover:underline flex-shrink-0"
          >
            Open Overlap →
          </button>
        </div>
      )}

      {content}

      {inviteProjectIds && (
        <BulkInviteDialog
          selectedProjectIds={inviteProjectIds}
          projects={inbound}
          activeAggregatorId={activeAggregatorId}
          onClose={() => {
            setInviteProjectIds(null)
            invalidatePortfolio()
          }}
        />
      )}
    </div>
  )
}
